import React from 'react';
import { AlertTriangle, HelpCircle, Trash2, Info } from 'lucide-react';
import { UnifiedModal } from './UnifiedModal';
import { UnifiedButton } from './UnifiedButton';

/**
 * UnifiedConfirmDialog — one-size-fits-most "are you sure?" dialog.
 *
 * Replaces the many ad-hoc confirm modals scattered across the admin
 * console (delete user, reject request, freeze subscription…). Each
 * used to render its own overlay + modal box + button row with
 * subtly different paddings, focus rings, and destructive-red shades.
 *
 * Props:
 *  - open, onClose
 *  - tone: 'danger' | 'warning' | 'info' | 'question' (default: 'question')
 *  - title, description, note (optional supporting text)
 *  - confirmLabel, cancelLabel
 *  - onConfirm — async fn, dialog disables buttons while pending
 *  - loading  — override the internal pending state (for parent-driven flows)
 *  - testId
 */
export const UnifiedConfirmDialog = ({
  open,
  onClose,
  tone = 'question',
  title,
  description,
  note,
  confirmLabel,
  cancelLabel = 'إلغاء',
  onConfirm,
  loading: externalLoading,
  testId = 'unified-confirm',
}) => {
  const [pending, setPending] = React.useState(false);
  const busy = externalLoading ?? pending;

  const toneMap = {
    danger:   { icon: Trash2,        color: 'var(--mesp-danger)',   bg: 'var(--mesp-danger-soft)',  variant: 'danger' },
    warning:  { icon: AlertTriangle, color: 'var(--mesp-warning)',  bg: 'var(--mesp-warning-soft)', variant: 'primary' },
    info:     { icon: Info,          color: 'var(--mesp-info)',     bg: 'var(--mesp-info-soft)',    variant: 'primary' },
    question: { icon: HelpCircle,    color: 'var(--mesp-accent)',   bg: 'var(--mesp-gold-soft-2)',  variant: 'primary' },
  }[tone];

  const Icon = toneMap.icon;

  const handleConfirm = async () => {
    if (externalLoading !== undefined) {
      // Parent controls loading — just fire and let them close.
      onConfirm && onConfirm();
      return;
    }
    setPending(true);
    try {
      await onConfirm?.();
    } finally {
      setPending(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={busy ? undefined : onClose}
      size="sm"
      title={null}
      testId={testId}
      footer={(
        <>
          <UnifiedButton
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            testId={`${testId}-cancel`}
          >
            {cancelLabel}
          </UnifiedButton>
          <UnifiedButton
            variant={toneMap.variant}
            onClick={handleConfirm}
            disabled={busy}
            testId={`${testId}-confirm`}
          >
            {busy ? 'جارٍ التنفيذ…' : (confirmLabel || 'تأكيد')}
          </UnifiedButton>
        </>
      )}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          aria-hidden
          style={{
            width: 56,
            height: 56,
            margin: '0 auto var(--mesp-space-4)',
            borderRadius: 'var(--mesp-radius-pill)',
            background: toneMap.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: toneMap.color,
          }}
        >
          <Icon size={26} strokeWidth={1.8} />
        </div>
        {title && (
          <h3
            style={{
              margin: 0,
              marginBottom: description ? 'var(--mesp-space-2)' : 0,
              fontSize: 'var(--mesp-font-h4)',
              fontWeight: 'var(--mesp-font-weight-semibold)',
              color: 'var(--mesp-text)',
            }}
            data-testid={`${testId}-title`}
          >
            {title}
          </h3>
        )}
        {description && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--mesp-font-sm)',
              color: 'var(--mesp-text-muted)',
              lineHeight: 1.6,
            }}
          >
            {description}
          </p>
        )}
        {note && (
          <div
            style={{
              marginTop: 'var(--mesp-space-4)',
              padding: 'var(--mesp-space-3) var(--mesp-space-4)',
              background: 'var(--mesp-bg-sunken)',
              border: '1px solid var(--mesp-border)',
              borderRadius: 'var(--mesp-radius-md)',
              fontSize: 'var(--mesp-font-xs)',
              color: 'var(--mesp-text-muted)',
              textAlign: 'start',
              lineHeight: 1.6,
            }}
          >
            {note}
          </div>
        )}
      </div>
    </UnifiedModal>
  );
};

export default UnifiedConfirmDialog;
